import { MAX_SIZE_OPTION_NAME_LENGTH } from '@repo/api-contracts';
import { GripVertical } from 'lucide-react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactElement } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import { Button, Input } from '@/shared/ui';
import type { SizeFormValues } from '../model/size-form';

interface SizeRowProps {
  readonly index: number;
  readonly rowCount: number;
  readonly register: UseFormRegister<SizeFormValues>;
  readonly handleProps: ButtonHTMLAttributes<HTMLButtonElement>;
  readonly rowProps: HTMLAttributes<HTMLTableRowElement>;
  readonly dropEdge?: 'before' | 'after' | undefined;
  readonly onMove: (from: number, to: number) => void;
  readonly onRemove: (index: number) => void;
}

/** 사이즈 옵션 표의 한 행 — 손잡이를 끌어 순서를 바꾸고(방향키도 된다), 이름을 고치거나 삭제한다. */
export const SizeRow = ({
  index,
  rowCount,
  register,
  handleProps,
  rowProps,
  dropEdge,
  onMove,
  onRemove,
}: SizeRowProps): ReactElement => (
  <tr
    {...rowProps}
    className={`border-t align-middle ${dropEdge === 'before' ? 'shadow-[inset_0_2px_0_0_var(--color-primary)]' : ''} ${dropEdge === 'after' ? 'shadow-[inset_0_-2px_0_0_var(--color-primary)]' : ''}`}
  >
    <td className="px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          {...handleProps}
          type="button"
          className="cursor-grab rounded p-1 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
          aria-label={`사이즈 ${index + 1} 순서 이동`}
          title="끌어서 순서 변경 · 방향키로 이동"
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp' && index > 0) {
              event.preventDefault();
              onMove(index, index - 1);
            }
            if (event.key === 'ArrowDown' && index < rowCount - 1) {
              event.preventDefault();
              onMove(index, index + 1);
            }
          }}
        >
          <GripVertical size={14} aria-hidden="true" />
        </button>
        <span className="tabular-nums">{index + 1}</span>
      </div>
    </td>
    <td className="px-3 py-2">
      <Input
        aria-label={`사이즈 ${index + 1} 이름`}
        className="h-8 w-32"
        placeholder="M"
        maxLength={MAX_SIZE_OPTION_NAME_LENGTH}
        {...register(`sizeOptions.${index}.name`, { required: true })}
      />
    </td>
    <td className="px-3 py-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive"
        aria-label={`사이즈 ${index + 1} 삭제`}
        onClick={() => onRemove(index)}
      >
        삭제
      </Button>
    </td>
  </tr>
);
